import { GraphQLError } from "graphql"

import { getCustomer } from "@/graphql/schemas/shared/utils/getCustomer"
import { CommerceService } from "@/services/commerce"

import { internalCustomerException } from "../../customer/utils/customerQueryHelpers"
import { checkPermissions, validateAccessRightsToCustomer } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { findCustomerTargetAddress, formCustomerCustomFieldsAction } from "../utils/customerAddressInformation"
import { addressValidation, getShippingAddressSchema } from "../validation/addressValidation"

export const mutationUpdateCustomerAddress = {
  async updateCustomerAddress(parent, args, context, info) {
    const { customerId, customerKey, customerNumber, addressId, values } = args
    const commerceApi = CommerceService.api()
    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.ADDRESSES,
      operation: Operations.UPDATE,
    })

    const customer = await getCustomer({ id: customerId, key: customerKey, number: customerNumber })
    await internalCustomerException({ id: customer.id })

    await validateAccessRightsToCustomer({
      user: user,
      customerNumber: customer.customerNumber,
    })

    // current address to update
    const targetAddress = findCustomerTargetAddress(customer, addressId)

    if (!targetAddress) return null

    // can't update billing address
    if (targetAddress.isBillingAddress || targetAddress.isDefaultBilling)
      throw new GraphQLError("can't update billing address", {
        extensions: {
          code: "FORBIDDEN",
        },
      })

    // validate inputs
    try {
      await addressValidation().validate(values)
      if (targetAddress.isShippingAddress) {
        await getShippingAddressSchema().validate(values)
      }
    } catch (error) {
      throw new Error(error.message)
    }

    // if new value is set, add it to the object, otherwise use the existing value
    const addressUpdateObject = {
      ...targetAddress,
      ...values,
    }

    let targetStatus = targetAddress?.custom?.fields?.status
    if (values?.customFields?.status) {
      switch (values?.customFields?.status) {
        case "DELETED":
          targetStatus = "X"
          break
        case "CLOSED":
          targetStatus = "S"
          break
        default:
          targetStatus = "A"
      }
    }

    const payload = {
      version: customer.version,
      actions: [
        await formCustomerCustomFieldsAction(addressUpdateObject, targetStatus, {
          creationBy: targetAddress?.custom?.fields?.creationBy,
          creationAt: targetAddress?.custom?.fields?.creationAt,
          modifiedBy: user.employeeNumber,
          versionNr: targetAddress?.custom?.fields?.versionNr,
          benefitRecipient: values?.customFields?.benefitRecipient,
          dateOfBirth: values?.customFields?.dateOfBirth || "",
        }),
      ],
    }

    try {
      const customerWithUpdatedAddress = (
        await commerceApi
          .customers()
          .withId({ ID: customer.id })
          .post({
            body: JSON.stringify(payload),
          })
          .execute()
      )?.body

      const updatedTargetAddress = findCustomerTargetAddress(customerWithUpdatedAddress, addressId)

      return updatedTargetAddress
    } catch (error) {
      throw new Error(error)
    }
  },
}
